<template>
  <!--
    Per-field help icon. Toggles the studio AI chat panel for THIS
    field. Visual states:

      idle  → ``?`` glyph, gold accent. Click opens the chat with
              this field's context.
      active → ``×`` glyph, dismiss accent. The chat panel is open
               AND scoped to this field; click closes the panel.

    Switching to a DIFFERENT field's icon while the panel is open
    just swaps the active field (panel stays open). Blur on the
    paired input refreshes the active field while the panel is open.

    Usage:
      <label>
        <span class="label">
          Voice
          <FieldHelpIcon
            field="voice" label="Voice" panel="identity"
            :value="form.voice"
          />
        </span>
        <textarea
          v-model="form.voice"
          @blur="dispatchFieldContext({
            field: 'voice', label: 'Voice', panel: 'identity',
            value: form.voice, source: 'blur',
          })"
        />
      </label>
  -->
  <button
    type="button"
    class="field-help-icon"
    :class="[`variant-${variant}`, { 'is-active': isActive }]"
    :title="resolvedTitle"
    :aria-label="resolvedTitle"
    @click.stop="onClick"
  >
    <i v-if="variant === 'pencil'" class="fas fa-pencil glyph-pencil"></i>
    <span v-else class="glyph">{{ glyph }}</span>
  </button>
</template>

<script setup>
import { computed } from 'vue'
import {
  dispatchFieldContext, studioAssistantState,
} from '@/utils/studio-assistant'

const props = defineProps({
  // Stable key used by the chat panel to dedupe context updates and
  // (in a later phase) to look up the field's helper-prompt template.
  field: { type: String, required: true },
  // Human label shown in the "Helping with: …" pill.
  label: { type: String, required: true },
  // Which Studio panel this field lives in (identity / skills / …).
  panel: { type: String, default: '' },
  // Current value of the field. Re-read on click so the chat panel
  // can show what's already there.
  value: { type: [String, Number], default: '' },
  // Optional override for the tooltip / aria-label. Falls back to a
  // generated string from ``label``.
  title: { type: String, default: '' },
  // Glyph variant. ``question`` (default) renders a ``?`` — used on
  // sectioned forms where the icon is purely a help-affordance.
  // ``pencil`` renders a ✏ — used on the public Profile card where
  // the icon doubles as an "edit this" affordance. Both flip to ×
  // when the chat panel is open and scoped to this field.
  variant: {
    type: String,
    default: 'question',
    validator: v => ['question', 'pencil'].includes(v),
  },
  // Optional extra context the assistant needs to act on this field
  // (e.g. { skillKey, role } so the position-name helper can fetch
  // reader-relevant suggestions). Opaque — passed straight through.
  meta: { type: Object, default: null },
})

// This icon is "active" when the chat panel is open AND its active
// field matches our ``field`` key. Drives the ?/× glyph swap and
// the dismiss-styled CSS variant.
const isActive = computed(() => (
  studioAssistantState.open
  && studioAssistantState.activeField?.field === props.field
))

const glyph = computed(() => {
  if (isActive.value) return '×'
  return props.variant === 'pencil' ? '✎' : '?'
})

const resolvedTitle = computed(() => {
  if (props.title) return props.title
  return isActive.value
    ? `Close Mystic Aida (currently helping with ${props.label})`
    : `Help me with ${props.label} from Mystic Aida`
})

function onClick() {
  dispatchFieldContext({
    field: props.field,
    label: props.label,
    panel: props.panel,
    value: props.value,
    source: 'click',
    meta: props.meta,
  })
}
</script>

<style scoped>
.field-help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0;
  background: rgba(201, 169, 78, 0.12);
  border: 1px solid rgba(201, 169, 78, 0.4);
  border-radius: 50%;
  color: #C9A94E;
  font-family: inherit;
  cursor: pointer;
  vertical-align: middle;
  transition: all 0.15s;
}
.field-help-icon:hover {
  background: rgba(201, 169, 78, 0.22);
  border-color: #C9A94E;
  transform: scale(1.08);
}
/* Active = chat panel is open and scoped to THIS field. Switch to
   a dismiss accent so the operator reads "click to close". */
.field-help-icon.is-active {
  background: rgba(244, 67, 54, 0.18);
  border-color: rgba(244, 67, 54, 0.7);
  color: rgb(255, 130, 120);
}
.field-help-icon.is-active:hover {
  background: rgba(244, 67, 54, 0.32);
  border-color: rgb(255, 130, 120);
}
.glyph {
  font-family: 'Cinzel', serif;
  font-size: 12px;
  line-height: 1;
}
.field-help-icon.is-active .glyph {
  /* × renders a touch larger than ? at the same font-size; trim
     the visual difference so the icon doesn't grow when toggled. */
  font-size: 14px;
  font-weight: 700;
}

/* Pencil variant — the universal Altar Mirror pencil: a minimal violet
   fa-pencil with no disc. Violet at rest, gold on hover, dismiss-red when its
   scoped chat is active. (The ``question`` help variant keeps its brass ?.) */
.field-help-icon.variant-pencil {
  width: auto;
  height: auto;
  margin-left: 6px;
  background: none;
  border: none;
  border-radius: 0;
  color: #9b86c9;
}
.field-help-icon.variant-pencil:hover {
  background: none;
  border-color: transparent;
  color: #f0d9a3;
  transform: scale(1.08);
}
.field-help-icon.variant-pencil.is-active { color: rgb(255, 130, 120); }
.field-help-icon.variant-pencil.is-active:hover { background: none; }
.glyph-pencil { font-size: 13px; line-height: 1; }
</style>
